const { getElement } = window.BAO.Utils

/**
 * Release countdown customer type
 * @namespace CUSTOMER_TYPES
 * @constant {Object}
 */
const CUSTOMER_TYPES = {
  General: 'general',
  Member: 'member',
}

/**
 * Countdown event types for the BAO countdown system
 * @namespace BAO_RELEASE_COUNTDOWN_EVENTS
 * @constant {Object}
 */
const BAO_RELEASE_COUNTDOWN_EVENTS = {
  /** @type {string} - Fired every second during countdown */
  INTERVAL: 'bao:release-countdown:interval',
  /** @type {string} - Fired when countdown reaches zero */
  EXPIRED: 'bao:release-countdown:expired',
}

// Initialize BAO namespace structure
window.BAO = window.BAO || {}
window.BAO.ReleaseCountdown = window.BAO.ReleaseCountdown || {}

/**
 * Global release countdown events reference
 * @type {Object}
 */
window.BAO.ReleaseCountdown.events = BAO_RELEASE_COUNTDOWN_EVENTS

/**
 * Initialize global release countdown interval if not already running
 * Dispatches INTERVAL events every second to all countdown components
 */
if (!window.BAO.ReleaseCountdown.intervalId) {
  /**
   * Global interval ID for countdown system
   * @type {number}
   */
  window.BAO.ReleaseCountdown.intervalId = setInterval(() => {
    document.dispatchEvent(
      new CustomEvent(BAO_RELEASE_COUNTDOWN_EVENTS.INTERVAL),
    )
  }, 1000)
}

/**
 * Custom button element that enables/disables based on release countdown expiration
 * Extends HTMLButtonElement to provide countdown-aware add-to-cart functionality
 *
 * @class ReleaseCountdownAtcButton
 * @extends {HTMLButtonElement}
 *
 * @example
 * <button
 *   is="release-countdown-atc-button"
 *   data-product-id="12345"
 *   data-is-available="true"
 *   data-customer-type="member"
 *   data-coming-soon-text="Coming Soon"
 *   data-members-only-text="Stag members Only"
 *   data-add-to-cart-text="Add to Cart"
 *   data-has-member-release-date="true"
 * >
 *   Add to Cart
 * </button>
 */
class ReleaseCountdownAtcButton extends window.BAO.CustomElement({
  SuperClass: HTMLButtonElement,
}) {
  /**
   * Required data attributes for the component
   * @static
   * @returns {string[]} Array of required attribute names
   */
  static get requiredAttributes () {
    return [
      'data-product-id',
      'data-is-available',
      'data-customer-type',
      'data-coming-soon-text',
      'data-members-only-text',
      'data-add-to-cart-text',
      'data-has-member-release-date',
    ]
  }

  /**
   * Initialize component when connected to DOM
   * Sets up member colors and initial button state
   * @override
   */
  connectedCallback () {
    super.connectedCallback()

    this.toggleMemberColours(this.dataset.hasMemberReleaseDate === 'true')
    this.setButtonState()

    const key = `has-expired-${this.dataset.productId}-${this.dataset.customerType}`
    const expiredState = JSON.parse(window.sessionStorage.getItem(key) || '{}')

    if (
      expiredState.countdownType &&
      expiredState.productId &&
      this[`${this.dataset.customerType}CountdownExpired`]
    ) {
      this.handleCountdownExpiredDetail(expiredState)
    } else {
      window.sessionStorage.removeItem(key)
    }
  }

  /**
   * Set up event listeners for countdown and product option changes
   * @protected
   * @override
   */
  setupListeners () {
    super.setupListeners()

    // Listen for countdown expiration events
    this.listeners.add(
      document,
      BAO_RELEASE_COUNTDOWN_EVENTS.EXPIRED,
      this.handleCountdownExpired.bind(this),
    )

    // Listen for product variant/option changes
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PRODUCT.OPTION_CHANGED,
      this.handleOptionChange.bind(this),
    )
  }

  /**
   * Handle product option/variant changes
   * Updates availability status based on selected variant
   *
   * @param {CustomEvent} event - Product option change event
   * @param {Object} event.detail - Event details
   * @param {Object} event.detail.variant - Selected product variant
   * @param {boolean} event.detail.variant.available - Variant availability status
   */
  handleOptionChange (event) {
    this.dataset.isAvailable = Boolean(event.detail.variant?.available)

    this.setButtonState()
  }

  /**
   * Handle countdown expiration events
   * Enables the button when countdown expires for matching product and customer type
   *
   * @param {CustomEvent} event - Countdown expired event
   * @param {Object} event.detail - Event details
   * @param {string} event.detail.productId - Product identifier
   * @param {string} event.detail.countdownType - Customer type (e.g., 'member', 'general')
   * @param {Date} event.detail.releaseDate - Original release date
   */

  handleCountdownExpired (event) {
    this.handleCountdownExpiredDetail(event.detail)
  }

  handleCountdownExpiredDetail (detail) {
    const productMatches = detail.productId === this.dataset.productId
    const typesMatch = detail.countdownType === this.dataset.customerType

    if (productMatches) {
      const key = `has-expired-${this.dataset.productId}-${this.dataset.customerType}`
      window.sessionStorage.setItem(key, JSON.stringify(detail))

      this.canEnable = typesMatch

      if (detail.countdownType === CUSTOMER_TYPES.Member) {
        this.memberCountdownExpired = true
      }

      if (detail.countdownType === CUSTOMER_TYPES.General) {
        this.generalCountdownExpired = true
      }
    }

    this.setButtonState()
  }

  /**
   * Set the button's enabled/disabled state and update text content
   * Updates button state based on availability and countdown status
   */
  setButtonState () {
    if (this.dataset.isAvailable !== 'true') return

    this.toggleAttribute('disabled', !this.canEnable)

    const textEl = getElement('[data-product-add-to-cart-text]', {
      context: this,
    })
    if (!textEl) {
      console.error(
        'ReleaseCountdownAtcButton: Missing text element with [data-product-add-to-cart-text]',
      )

      return
    }

    const customerIsMember =
      this.dataset.customerType === CUSTOMER_TYPES.Member

    const hasMemberReleaseDate = this.dataset.hasMemberReleaseDate === 'true'
    const hasGeneralReleaseDate = this.dataset.hasGeneralReleaseDate === 'true'

    const customerText =
      !customerIsMember && hasMemberReleaseDate && !hasGeneralReleaseDate
        ? this.dataset.membersOnlyText
        : this.dataset.comingSoonText

    textEl.textContent = this.canEnable
      ? this.dataset.addToCartText
      : customerText
  }

  /**
   * Toggle member color scheme on the document body
   * @param {boolean} value - Whether to enable member colors
   */
  toggleMemberColours (value) {
    document.body.dataset.releaseCountdownMemberColours = value
      ? 'true'
      : 'false'
  }

  /**
   * Get whether the button can be enabled
   * @returns {boolean} True if button can be enabled
   */
  get canEnable () {
    return this._canEnable || false
  }

  /**
   * Set whether the button can be enabled
   * @param {boolean} value - Enable state
   */
  set canEnable (value) {
    this._canEnable = value
  }

  /**
   * Get whether the member countdown has expired
   * @returns {boolean} True if member countdown has expired
   */
  get memberCountdownExpired () {
    return this._memberCountdownExpired || false
  }

  /**
   * Set whether the member countdown has expired
   * @param {boolean} value - Member countdown expiration state
   */
  set memberCountdownExpired (value) {
    this._memberCountdownExpired = value
  }

  /**
   * Get whether the general countdown has expired
   * @returns {boolean} True if general countdown has expired
   */
  get generalCountdownExpired () {
    return this._generalCountdownExpired || false
  }

  /**
   * Set whether the general countdown has expired
   * Also toggles member colors when general countdown expires
   * @param {boolean} value - General countdown expiration state
   */
  set generalCountdownExpired (value) {
    this._generalCountdownExpired = value

    this.toggleMemberColours(!value)
  }
}

if (!window.customElements.get('release-countdown-atc-button')) {
  window.customElements.define(
    'release-countdown-atc-button',
    ReleaseCountdownAtcButton,
    {
      extends: 'button',
    },
  )
}

/**
 * Custom element that displays a countdown timer for product releases
 * Shows different formats based on time remaining (date, hours:minutes, minutes:seconds)
 *
 * @class ReleaseCountdown
 * @extends {HTMLElement}
 *
 * @example
 * <release-countdown
 *   data-product-id="12345"
 *   data-countdown-type="member"
 *   data-release-date="2024-12-25T10:00:00Z"
 *   data-hours-label="H"
 *   data-minutes-label="M"
 *   data-seconds-label="S"
 * >
 *   <span data-release-countdown-el="timer"></span>
 * </release-countdown>
 */
class ReleaseCountdown extends window.BAO.CustomElement() {
  /**
   * Required child elements
   * @static
   * @returns {string[]} Array of required element names
   */
  static get requiredElements () {
    return ['timer']
  }

  /**
   * Required data attributes for the component
   * @static
   * @returns {string[]} Array of required attribute names
   */
  static get requiredAttributes () {
    return [
      'data-product-id',
      'data-countdown-type',
      'data-release-date',
      'data-hours-label',
      'data-minutes-label',
      'data-seconds-label',
    ]
  }

  /**
   * Set up event listeners for release countdown intervals
   * @protected
   * @override
   */
  setupListeners () {
    super.setupListeners()

    this.render = this.render.bind(this)
    this.listeners.add(
      document,
      BAO_RELEASE_COUNTDOWN_EVENTS.INTERVAL,
      this.render,
    )
  }

  /**
   * Initialize component when connected to DOM
   * Parses release date and starts initial render
   * @override
   */
  connectedCallback () {
    super.connectedCallback()

    /**
     * Target release date
     * @type {Date}
     */
    this.releaseDate = new Date(this.dataset.releaseDate)
    this.render()
  }

  /**
   * Render the countdown timer display
   * Updates timer element with appropriate format based on time remaining
   * @protected
   */
  render () {
    if (!this.isValidDate(this.releaseDate)) return

    const timerEl = this.els.timer.element
    const timeRemaining = this.timeRemaining()

    // Check if countdown has expired
    this.hasExpired = !timerEl || timeRemaining.totalSeconds <= 0

    if (this.hasExpired) return

    // More than 24 hours: show date
    if (timeRemaining.isMoreThanOneDay) {
      timerEl.textContent = `${this.releaseDate.toLocaleDateString()}`
      return
    }

    // 1-24 hours: show hours and minutes
    if (timeRemaining.isMoreThanOneHour) {
      timerEl.textContent = `${timeRemaining.hours}${this.dataset.hoursLabel} : ${timeRemaining.minutes}${this.dataset.minutesLabel}`
      return
    }

    // Less than 1 hour: show minutes and seconds
    timerEl.textContent = `${timeRemaining.minutes}${this.dataset.minutesLabel} : ${timeRemaining.seconds}${this.dataset.secondsLabel}`
  }

  /**
   * Calculate time remaining until release
   * @returns {TimeRemaining} Object containing formatted time components and status flags
   */
  timeRemaining () {
    const now = new Date()
    const releaseDate = new Date(this.releaseDate)
    const totalSeconds = Math.floor((releaseDate - now) / 1000)
    const hoursRemaining = Math.floor(totalSeconds / 3600)

    return {
      /** @type {string} Hours remaining (zero-padded) */
      hours: String(hoursRemaining).padStart(2, '0'),
      /** @type {string} Minutes remaining (zero-padded) */
      minutes: String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0'),
      /** @type {string} Seconds remaining (zero-padded) */
      seconds: String(totalSeconds % 60).padStart(2, '0'),
      /** @type {boolean} True if 24 or more hours remain */
      isMoreThanOneDay: hoursRemaining >= 24,
      /** @type {boolean} True if 1 or more hours remains */
      isMoreThanOneHour: hoursRemaining >= 1,
      /** @type {number} Total seconds remaining */
      totalSeconds,
    }
  }

  /**
   * Validates that a given value is a valid Date object
   *
   * @param {*} dateValue - The value to validate (can be any type)
   * @returns {boolean} True if the value is a valid Date object, false otherwise
   */
  isValidDate (dateValue) {
    // Check if the value is an instance of Date
    if (!(dateValue instanceof Date)) {
      return false
    }

    // Check if the Date object contains a valid date
    // Invalid Date objects have a NaN time value
    return !isNaN(dateValue.getTime())
  }

  /**
   * Get countdown expiration status
   * @returns {boolean} True if countdown has expired
   */
  get hasExpired () {
    return this._hasExpired || false
  }

  /**
   * Set countdown expiration status
   * When set to true, hides component and dispatches expiration event
   *
   * @param {boolean} value - Expiration status
   * @fires BAO_RELEASE_COUNTDOWN_EVENTS.EXPIRED
   */
  set hasExpired (value) {
    this._hasExpired = value
    this.setAttribute('aria-hidden', value ? 'true' : 'false')

    if (!value) return

    // Stop listening to intervals when expired
    this.listeners.remove(BAO_RELEASE_COUNTDOWN_EVENTS.INTERVAL, this.render)

    // Dispatch expiration event
    document.dispatchEvent(
      new CustomEvent(BAO_RELEASE_COUNTDOWN_EVENTS.EXPIRED, {
        detail: {
          /** @type {string} Product identifier */
          productId: this.dataset.productId,
          /** @type {string} Customer type/countdown type */
          countdownType: this.dataset.countdownType,
          /** @type {Date} Release date that expired */
          releaseDate: this.releaseDate,
        },
      }),
    )
  }
}

if (!window.customElements.get('release-countdown')) {
  window.customElements.define('release-countdown', ReleaseCountdown)
}

/**
 * @typedef {Object} TimeRemaining
 * @property {string} hours - Hours remaining (zero-padded string)
 * @property {string} minutes - Minutes remaining (zero-padded string)
 * @property {string} seconds - Seconds remaining (zero-padded string)
 * @property {boolean} isMoreThanOneDay - True if more than 24 hours remain
 * @property {boolean} isMoreThanOneHour - True if more than 1 hour remains
 * @property {number} totalSeconds - Total seconds remaining (can be negative)
 */
